import React from "react";
import { Sparkles } from "lucide-react";

export const LIMIT = 1500;
export const AREAS = [["problems", "Problems"], ["medications", "Medications"], ["allergies", "Allergies"], ["surgeries", "Surgeries"], ["family_history", "Family history"], ["social_history", "Social history"]];
export const TYPE_LABEL = { yes_no: "Yes / No", single_select: "Pick one", multi_select: "Pick any", text: "Typed answer" };
export const DEPTH_LABEL = { mini: "Mini", detailed: "Detailed" };
export const CAP = { mini: 6, detailed: 12 };
export const CATS = [["reason", "Reason"], ["safety", "Safety"], ["history", "History"], ["medications", "Medications"], ["allergies", "Allergies"], ["screening", "Screening"], ["specialty", "Specialty"], ["general", "General"]];
export const catKey = (c) => { const k = String(c || "general").toLowerCase(); return CATS.some(([x]) => x === k) ? k : "general"; };
export const catLabel = (c) => CATS.find(([k]) => k === catKey(c))[1];
export const isChoice = (t) => t === "single_select" || t === "multi_select";
// Strip local-only keys and empty options before sending a set to the API.
// Blank questions are dropped; a choice with fewer than 2 options goes out as a typed answer (as the row warns).
export const cleanQs = (qs) => (qs || []).filter((q) => String(q.question || "").trim()).map(({ _k, ...q }) => {
  const options = (q.options || []).map((o) => String(o).trim()).filter(Boolean);
  const type = q.type in TYPE_LABEL ? (isChoice(q.type) && options.length < 2 ? "text" : q.type) : "text";
  return { ...q, question: String(q.question).trim(), type, category: catKey(q.category), new_patient_only: !!q.new_patient_only, options: type === "text" ? [] : type === "yes_no" && !options.length ? ["Yes", "No"] : options };
});
// Generate/preview are slow LLM calls (~20–40 s): tell a timeout/gateway error apart from an empty answer.
export const slowErr = (e) => e?.code === "ECONNABORTED" || [502, 504].includes(e?.response?.status);
export const IBASE = "/api/edi/televisit/provider-configs";
export const sig = (qs) => JSON.stringify(qs || []);
export const qList = (d) => (Array.isArray(d) ? d : d?.questions || d?.design?.questions || d?.intake_design?.questions || d?.form?.questions || []);

export function designSub(design) {
  const n = design?.questions?.length || 0;
  return n ? `${DEPTH_LABEL[design.depth] || "Mini"} · ${n} questions` : null;
}
export function intakeMeta(cfg) {
  const d = designSub(cfg?.intake_design);
  if (d) return { text: `${d}${cfg.intake_design.include_new_patient_history ? " · new-patient history" : ""}` };
  if ((cfg?.intake_guidance || "").trim()) return { text: "Direction only · no question set" };
  return { text: "Not set", faint: true };
}

export const IN_CSS = `@keyframes inGlow{0%,100%{box-shadow:0 0 0 1px rgba(88,86,214,.25),0 4px 18px rgba(88,86,214,.22)}50%{box-shadow:0 0 0 1px rgba(88,86,214,.45),0 6px 28px rgba(88,86,214,.5)}}
.in-mod{box-shadow:0 0 0 1px rgba(88,86,214,.25),0 4px 18px rgba(88,86,214,.25)}
.in-mod.busy{animation:inGlow 1.2s ease-in-out infinite}
.in-x{opacity:0;transition:opacity .12s,background-color .12s,color .12s}
.in-row:hover .in-qx,.in-row:focus-within .in-qx,.in-row:hover .in-ctl,.in-row:focus-within .in-ctl,.in-chip:hover .in-x,.in-chip:focus-within .in-x{opacity:1}
.in-qx:hover{background:#FCEBEA;color:#B3261E}
.in-ctl{opacity:0;transition:opacity .12s}
.in-sel{appearance:none;background:transparent;border:1px solid transparent;border-radius:6px;padding:0 4px;font-size:12px;color:var(--gray2);cursor:pointer;transition:border-color .12s,background-color .12s}
.in-sel:hover,.in-sel:focus{border-color:#D4D4DA;background:#fff;outline:none}
.in-opt{background:transparent;outline:none;min-width:3ch;font-size:12px;color:var(--ink2)}
.in-ta{resize:none;overflow:hidden;background:transparent;outline:none;width:100%}
@media (prefers-reduced-motion:reduce){.in-mod.busy{animation:none}.in-sweep{display:none}}`;

export function ModifyBtn({ busy, busyLabel, children, onClick, testid, disabled }) {
  return (
    <button type="button" onClick={onClick} disabled={busy || disabled} data-testid={testid}
      className={`in-mod ${busy ? "busy" : ""} inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3.5 py-2 rounded-[10px] bg-white text-[var(--ink)] disabled:cursor-default flex-none`}>
      <Sparkles size={13} style={{ color: "#5856D6" }} />{busy ? busyLabel : children}
    </button>
  );
}

export const SweepBar = ({ testid }) => (
  <div className="in-sweep relative h-[3px] rounded-full bg-[rgba(88,86,214,.12)] overflow-hidden" data-testid={testid}><span className="revq-bar-sweep" /></div>
);

export const Strip = ({ children, testid }) => (
  <div className="rounded-[14px] border border-[#E9E9EC] bg-white flex flex-col gap-3" style={{ padding: "14px 16px" }} data-testid={testid}>{children}</div>
);

export const Faint = ({ children, testid }) => <div className="py-10 text-center text-[13px] text-[var(--gray3)]" data-testid={testid}>{children}</div>;
